const CLASSIFICATION_PRIORITIES: Record<string, number> = {
  Target: 0,
  High: 10,
  "Very High": 100,
  "Critical High": 1000,
  Low: 10,
  "Very Low": 100,
  "Critical Low": 1000,
};

/**
 * Returns the most severe of the given classifications, as a sortable priority and its label.
 *
 * Each tier's high and low sides share a priority — `High`/`Low`, `Very High`/`Very Low`,
 * `Critical High`/`Critical Low` — so the vitals table sorts by severity magnitude rather than
 * direction. A tie between the two therefore resolves to whichever comes first in `classifications`.
 * Unrecognized classifications are ignored; an empty list yields `{ priority: -1, label: "" }`.
 */
export const getHighestClassification = <T extends { classification: string }>(classifications: T[]) =>
  classifications.reduce<{ priority: number; label: string }>(
    (acc, curr) => {
      const newPriority = CLASSIFICATION_PRIORITIES[curr.classification];
      if (newPriority !== undefined && newPriority > acc.priority) {
        return {
          priority: newPriority,
          label: curr.classification,
        };
      }
      return acc;
    },
    { priority: -1, label: "" }
  );

/**
 * Resolves a single classification for an observation based on selected threshold config IDs.
 * - If no thresholds selected: returns the first classification
 * - If exactly 1 classification: returns it unconditionally
 * - If multiple: returns the first matching a selected threshold
 */
export const resolveClassificationForThresholds = <T extends { configurationId: string }>(
  classifications: T[],
  selectedThresholdIds: string[] | undefined
): T | undefined => {
  if (!selectedThresholdIds?.length) return classifications[0];
  if (classifications.length === 1) return classifications[0];
  return classifications.find((c) => selectedThresholdIds.includes(c.configurationId));
};

/**
 * Maps an observation's measurements to include their resolved classification based on the selected
 * threshold config IDs.
 */
export const getMeasurementsWithClassifications = <
  Measurement extends { observationId: string },
  Classification extends { observationId: string; configurationId: string },
>(
  observation: { measurements: Measurement[]; classifications: Classification[] },
  selectedThresholdIds: string[] | undefined
) =>
  observation.measurements.map((measurement) => {
    const observationClassifications = observation.classifications.filter(
      (c) => c.observationId === measurement.observationId
    );
    const classification = resolveClassificationForThresholds(observationClassifications, selectedThresholdIds);
    return { measurement, classification };
  });
